import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Users, Check, AlertCircle, ChefHat, PartyPopper, HeartHandshake, X } from 'lucide-react';
import ethiopianFeastImg from '../assets/ethiopian-feast.png';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

export default function CateringSection() {
  const { t, i18n } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: 'Wedding',
    eventDate: '',
    guestCount: '',
    foodOptions: {}, // Will be dynamic
    specialRequests: ''
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  const [rawProducts, setRawProducts] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);

  const fetchFoodItems = useCallback(async (nextPage = 1, append = false) => {
    if (!append) setItemsLoading(true);
    else setPageLoading(true);
    
    try {
      const response = await api.get('/products', {
        params: { 
          category: 'Food,Beverage',
          limit: 10,
          page: nextPage
        }
      });
      
      const newItems = response.data || [];
      if (newItems.length > 0) {
          setRawProducts(prev => append ? [...prev, ...newItems] : newItems);
          
          setFormData(prev => {
              const newFoodOptions = {};
              newItems.forEach(item => {
                  if (prev.foodOptions[item.id] === undefined) {
                      newFoodOptions[item.id] = false;
                  }
              });
              return {
                  ...prev,
                  foodOptions: { ...prev.foodOptions, ...newFoodOptions }
              };
          });
          
          setHasMore(newItems.length === 10);
      } else {
          setHasMore(false);
      }
    } catch (error) {
      console.error('Failed to fetch food items:', error);
    } finally {
      setItemsLoading(false);
      setPageLoading(false);
    }
  }, []); // formData dependency removed via functional update

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchFoodItems(nextPage, true);
  };

  React.useEffect(() => {
    fetchFoodItems();
  }, [fetchFoodItems]);

  const displayFoodItems = useMemo(() => {
    return rawProducts.map(product => ({
      id: product.id,
      label: i18n.language === 'am' ? (product.name_am || product.name) : product.name
    }));
  }, [rawProducts, i18n.language]);

  if (itemsLoading) {
      // Optional: Add a loading state here if needed, or just let it render empty first
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (id) => {
    setFormData(prev => ({
      ...prev,
      foodOptions: { ...prev.foodOptions, [id]: !prev.foodOptions[id] }
    }));
  };

  const validateDate = (dateString) => {
    const eventDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);
    
    // Calculate difference in days
    const diffTime = eventDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays >= 15;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    if (!validateDate(formData.eventDate)) {
      setStatus({ 
        type: 'error', 
        message: 'Catering orders must be placed at least 15 days in advance.' 
      });
      setLoading(false);
      return;
    }

    try {
      await api.post('/catering', formData);

      setStatus({ 
        type: 'success', 
        message: t('catering.modal.success') 
      });
      setTimeout(() => {
        setIsModalOpen(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          eventType: 'Wedding',
          eventDate: '',
          guestCount: '',
          foodOptions: {},
          specialRequests: ''
        });
        setStatus({ type: '', message: '' });
      }, 3000);

    } catch (error) {
      console.error('Error submitting form:', error);
      const errorMessage = error.response?.data?.error || t('catering.modal.errorGeneric');
      setStatus({ type: 'error', message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="font-sans">
      {/* Hero Section */}
      {/* Hero Section */}
      <div className="relative bg-raya-bg text-raya-text-primary overflow-hidden transition-colors duration-500">
        <div className="absolute inset-0 opacity-5 dark:opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
        
        {/* Subtle Gradient Accent */}
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-raya-accent/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute top-1/2 right-0 w-64 h-64 bg-raya-accent/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="container mx-auto px-4 py-24 relative z-10 flex flex-col md:flex-row items-center">
          <div className="md:w-1/2 mb-12 md:mb-0">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block py-1.5 px-4 rounded-full bg-raya-accent text-black text-xs md:text-sm font-bold mb-6 shadow-sm">
                ✨ {t('catering.badge')} ✨
              </span>
              <h2 className="text-4xl md:text-5xl font-extrabold mb-6 leading-tight text-raya-text-primary">
                {t('catering.title')}
              </h2>
              <p className="text-lg md:text-xl text-raya-text-primary/80 mb-10 max-w-lg leading-relaxed">
                {t('catering.description')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <div className="flex items-start gap-3">
                  <div className="bg-raya-accent/10 p-2 rounded-lg">
                    <Users className="w-6 h-6 text-raya-accent" />
                  </div>
                  <div>
                    <h4 className="font-bold text-raya-text-primary">{t('catering.groupSize.title')}</h4>
                    <p className="text-sm text-raya-text-secondary font-medium">{t('catering.groupSize.desc')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="bg-emerald-100 dark:bg-emerald-900/50 p-2 rounded-lg">
                    <Calendar className="w-6 h-6 text-emerald-700 dark:text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="font-bold text-raya-text-primary">{t('catering.planAhead.title')}</h4>
                    <p className="text-sm text-raya-text-secondary font-medium">{t('catering.planAhead.desc')}</p>
                  </div>
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsModalOpen(true)}
                className="bg-raya-accent hover:bg-yellow-700 text-black font-bold py-4 px-8 rounded-full shadow-lg shadow-raya-accent/30 hover:shadow-raya-accent/50 flex items-center gap-2 text-lg transition-all"
              >
                <PartyPopper className="w-5 h-5" />
                {t('catering.cta')}
              </motion.button>
              
              <p className="mt-4 text-xs text-raya-text-secondary font-semibold flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                {t('catering.notice')}
              </p>
            </motion.div>
          </div>
          
            <div className="md:w-1/2 relative">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative z-10"
            >
              <img 
                src={ethiopianFeastImg} 
                alt="Ethiopian Catering Feast" 
                className="rounded-2xl shadow-2xl w-full object-cover transform rotate-2 hover:rotate-0 transition-transform duration-500 border-4 border-amber-400/30 dark:border-amber-500/20"
              />
              
              <motion.div 
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                transition={{ delay: 1, type: "spring" }}
                className="absolute -bottom-6 -left-6 bg-raya-card text-emerald-950 dark:text-emerald-50 p-4 rounded-xl shadow-xl border border-amber-100 dark:border-zinc-800 hidden md:block"
              >
                <div className="flex items-center gap-2 mb-1">
                  <HeartHandshake className="w-5 h-5 text-amber-500" />
                  <span className="font-extrabold pb-1 border-b-2 border-amber-400">{t('catering.imageBadge.line1')}</span>
                </div>
                <div className="text-sm font-medium">{t('catering.imageBadge.line2')}</div>
              </motion.div>
            </motion.div>
            
            {/* Decorative elements */}
            <div className="absolute top-10 -right-10 w-32 h-32 bg-amber-400/20 dark:bg-amber-500/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-10 left-10 w-40 h-40 bg-emerald-500/20 dark:bg-emerald-500/10 rounded-full blur-3xl"></div>
          </div>
        </div>
      </div>

      {/* Inquiry Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={() => setIsModalOpen(false)}
            />
            
            <motion.div 
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="relative w-full max-w-2xl bg-raya-card rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
            >
                {/* Modal Header */}
                <div className="sticky top-0 z-10 bg-raya-card/95 backdrop-blur-sm px-6 py-4 border-b border-raya-border flex items-center justify-between">
                  <div className="flex flex-col">
                    <h3 className="text-xl font-bold text-raya-text-primary">{t('catering.modal.title')}</h3>
                    <p className="text-sm text-raya-text-tertiary">{t('catering.modal.subtitle')}</p>
                  </div>
                  <button 
                    onClick={() => setIsModalOpen(false)}
                    className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-full transition-colors"
                  >
                        <X size={24} className="text-zinc-500" />
                    </button>
                </div>

                {/* Modal Body */}
                <div className="p-6">
                    {status.message && (
                        <div className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${
                            status.type === 'success' 
                                ? 'bg-green-50 text-green-700 border border-green-200' 
                                : 'bg-red-50 text-red-700 border border-red-200'
                        }`}>
                            {status.type === 'success' ? <Check size={20} /> : <AlertCircle size={20} />}
                            <p>{status.message}</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Contact Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-raya-text-secondary">{t('catering.modal.labels.name')}</label>
                                <input 
                                    type="text" 
                                    name="name" 
                                    value={formData.name} 
                                    onChange={handleInputChange} 
                                    required 
                                    className="w-full px-4 py-2 rounded-lg border border-raya-border bg-raya-bg-off focus:ring-2 focus:ring-raya-accent focus:outline-none transition-all text-raya-text-primary"
                                    placeholder={t('catering.modal.placeholders.name')}
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-raya-text-secondary">{t('catering.modal.labels.email')}</label>
                                <input 
                                    type="email" 
                                    name="email" 
                                    value={formData.email} 
                                    onChange={handleInputChange} 
                                    required 
                                    className="w-full px-4 py-2 rounded-lg border border-raya-border bg-raya-bg-off focus:ring-2 focus:ring-raya-accent focus:outline-none transition-all text-raya-text-primary"
                                    placeholder={t('catering.modal.placeholders.email')}
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-raya-text-secondary">{t('catering.modal.labels.phone')}</label>
                                <input 
                                    type="tel" 
                                    name="phone" 
                                    value={formData.phone} 
                                    onChange={handleInputChange} 
                                    required 
                                    className="w-full px-4 py-2 rounded-lg border border-raya-border bg-raya-bg-off focus:ring-2 focus:ring-raya-accent focus:outline-none transition-all text-raya-text-primary"
                                    placeholder={t('catering.modal.placeholders.phone')}
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-raya-text-secondary">{t('catering.modal.labels.eventType')}</label>
                                <select 
                                    name="eventType" 
                                    value={formData.eventType} 
                                    onChange={handleInputChange} 
                                    className="w-full px-4 py-2 rounded-lg border border-raya-border bg-raya-bg-off focus:ring-2 focus:ring-raya-accent focus:outline-none transition-all text-raya-text-primary"
                                >
                                    <option value="Wedding">{t('catering.modal.eventTypes.Wedding')}</option>
                                    <option value="Birthday">{t('catering.modal.eventTypes.Birthday')}</option>
                                    <option value="Corporate">{t('catering.modal.eventTypes.Corporate')}</option>
                                    <option value="Family Gathering">{t('catering.modal.eventTypes.Family')}</option>
                                    <option value="Other">{t('catering.modal.eventTypes.Other')}</option>
                                </select>
                            </div>
                        </div>

                        {/* Event Details */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-raya-text-secondary">{t('catering.modal.labels.eventDate')}</label>
                                <input 
                                    type="date" 
                                    name="eventDate" 
                                    value={formData.eventDate} 
                                    onChange={handleInputChange} 
                                    required 
                                    min={new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                                    className="w-full px-4 py-2 rounded-lg border border-raya-border bg-raya-bg-off focus:ring-2 focus:ring-raya-accent focus:outline-none transition-all text-raya-text-primary"
                                />
                                <p className="text-xs text-zinc-500">{t('catering.modal.dateHint')}</p>
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-raya-text-secondary">{t('catering.modal.labels.guestCount')}</label>
                                <input 
                                    type="number" 
                                    name="guestCount" 
                                    value={formData.guestCount} 
                                    onChange={handleInputChange} 
                                    required 
                                    min="10"
                                    className="w-full px-4 py-2 rounded-lg border border-raya-border bg-raya-bg-off focus:ring-2 focus:ring-raya-accent focus:outline-none transition-all text-raya-text-primary"
                                    placeholder={t('catering.modal.placeholders.guestCount')}
                                />
                            </div>
                        </div>

                        {/* Food Selection */}
                        <div className="space-y-3 p-5 rounded-2xl bg-raya-bg-off border border-raya-border">
                            <h4 className="text-sm font-bold text-raya-text-primary flex items-center gap-2">
                                <ChefHat size={16} className="text-emerald-600 dark:text-emerald-400" />
                                {t('catering.modal.foodSelection')}
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {displayFoodItems.map((item) => (
                                    <label key={item.id} className="group flex items-center gap-3 p-3 rounded-xl border border-raya-border bg-raya-card hover:border-emerald-500 dark:hover:border-emerald-500 cursor-pointer transition-all shadow-sm hover:shadow-md">
                                        <div className="relative flex items-center justify-center">
                                            <input 
                                                type="checkbox" 
                                                checked={formData.foodOptions[item.id]} 
                                                onChange={() => handleCheckboxChange(item.id)}
                                                className="peer appearance-none w-5 h-5 rounded border-2 border-gray-300 dark:border-gray-500 checked:bg-emerald-600 checked:border-emerald-600 transition-colors"
                                            />
                                            <Check size={12} className="absolute text-white opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity" />
                                        </div>
                                        <span className="text-sm font-medium text-raya-text-secondary group-hover:text-raya-text-primary transition-colors">{item.label}</span>
                                    </label>
                                ))}
                            </div>

                            {hasMore && (
                                <div className="flex justify-center mt-4">
                                    <button
                                        type="button"
                                        onClick={handleLoadMore}
                                        disabled={pageLoading}
                                        className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 transition-all border border-emerald-100 dark:border-emerald-800/50"
                                    >
                                        {pageLoading ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-emerald-600/30 border-t-emerald-600 rounded-full animate-spin" />
                                                <span>{t('products.loading', 'Loading...')}</span>
                                            </>
                                        ) : (
                                            <span>{t('products.loadMore', 'Load More')}</span>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Special Requests */}
                        <div className="flex flex-col gap-1.5 md:col-span-2">
                            <label className="text-sm font-medium text-raya-text-secondary">{t('catering.modal.labels.specialRequests')}</label>
                            <textarea 
                                name="specialRequests" 
                                value={formData.specialRequests} 
                                onChange={handleInputChange} 
                                rows="3" 
                                className="w-full px-4 py-2 rounded-lg border border-raya-border bg-raya-bg-off focus:ring-2 focus:ring-raya-accent focus:outline-none transition-all text-raya-text-primary"
                                placeholder={t('catering.modal.placeholders.specialRequests')}
                            ></textarea>
                        </div>

                        {/* Submit Button */}
                        <div className="pt-2">
                            <button 
                                type="submit" 
                                disabled={loading}
                                className="w-full bg-raya-accent text-black font-bold py-3 rounded-lg shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {loading ? t('catering.modal.labels.sending') : t('catering.modal.labels.submit')}
                            </button>
                        </div>
                    </form>
                </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
